
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DashboardStats } from "@/utils/dashboardUtils";

interface KPICardsProps {
  stats: DashboardStats;
  isLoading: boolean;
  percentages: {
    expiringSoonPercent: number;
    expiredPercent: number;
    healthyPercent: number;
  };
  timeFrame: string;
}

export default function KPICards({ stats, isLoading, percentages, timeFrame }: KPICardsProps) {
  if (isLoading) {
    return null; // Loading state is handled in parent component
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="py-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-t-lg">
          <CardTitle className="text-lg">Products Expiring Soon</CardTitle>
          <CardDescription>Within {timeFrame} days</CardDescription>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-amber-500">{stats.expiringSoon}</span>
            <span className="text-sm text-muted-foreground">Items</span>
          </div>
          <div className="mt-2">
            <Progress 
              value={percentages.expiringSoonPercent} 
              className="h-2 bg-amber-100" 
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{percentages.expiringSoonPercent}% of inventory</p>
          </div>
        </CardContent>
      </Card>
      
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="py-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-t-lg">
          <CardTitle className="text-lg">Recently Expired</CardTitle>
          <CardDescription>Last 7 days</CardDescription>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-red-500">{stats.recentlyExpired}</span>
            <span className="text-sm text-muted-foreground">Items</span>
          </div>
          <div className="mt-2">
            <Progress 
              value={percentages.expiredPercent} 
              className="h-2 bg-red-100" 
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{percentages.expiredPercent}% of inventory</p>
          </div>
        </CardContent>
      </Card>
      
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="py-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
          <CardTitle className="text-lg">Total Inventory</CardTitle>
          <CardDescription>All tracked items</CardDescription>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-blue-600">{stats.totalInventory}</span>
            <span className="text-sm text-muted-foreground">Items</span>
          </div>
          <div className="mt-4">
            {stats.totalInventory > 0 ? (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                  Healthy: {percentages.healthyPercent}%
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mr-1"></span>
                  Expiring: {percentages.expiringSoonPercent}%
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                  Expired: {percentages.expiredPercent}%
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center">No items in inventory</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="py-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-lg">
          <div className="flex items-center space-x-2">
            <CardTitle className="text-lg">Avg. Shelf Life</CardTitle>
            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Average remaining days until expiration across inventory</p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
          <CardDescription>Remaining days</CardDescription>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-emerald-500">{stats.averageShelfLife || 0}</span>
            <span className="text-sm text-muted-foreground">Days</span>
          </div>
          <div className="mt-2">
            {stats.averageShelfLife ? (
              <div className="mt-2">
                <div className="relative pt-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block text-emerald-600">
                        Health Score
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-emerald-600">
                        {Math.min(100, stats.averageShelfLife > 30 ? 100 : Math.round((stats.averageShelfLife / 30) * 100))}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-emerald-100">
                    <div 
                      style={{ width: `${Math.min(100, stats.averageShelfLife > 30 ? 100 : Math.round((stats.averageShelfLife / 30) * 100))}%` }} 
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-emerald-500">
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center mt-4">No items with expiry dates</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
